/**
 * Pure statistics over the attempts set (PLAN-Z M5): identical for guest
 * (IndexedDB) and logged-in (Postgres) data — one code path, always
 * recomputable, never stored as running totals (PLAN.md §6).
 *
 * Timing semantics (PLAN-Z M5): accuracy counts RESPONSES, timing counts
 * ITEMS. Figure items (M7) produce two responses sharing one itemId and one
 * durationMs; the timing distribution dedupes by itemId so one solve
 * contributes one data point.
 */
import type { Attempt } from '../data/local.ts'
import type { Difficulty, Subtest } from '../generators/types.ts'

export interface BucketStats {
  /** Scored responses (a figure item contributes two). */
  responses: number
  correct: number
  accuracy: number // 0..1
  /** Per-item timing (deduped by itemId). */
  items: number
  avgMs: number
  medianMs: number
  p90Ms: number
}

export type BucketKey = `${Subtest}/${Difficulty}`

export interface Summary {
  overall: BucketStats
  bySubtest: Partial<Record<Subtest, BucketStats>>
  byBucket: Partial<Record<BucketKey, BucketStats>>
}

function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0
  const pos = (sorted.length - 1) * q
  const lo = Math.floor(pos)
  const hi = Math.ceil(pos)
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo)
}

/** Timing sample: one duration per distinct itemId, first occurrence wins. */
function itemDurations(attempts: Attempt[]): number[] {
  const seen = new Set<string>()
  const out: number[] = []
  for (const a of attempts) {
    if (seen.has(a.itemId)) continue
    seen.add(a.itemId)
    out.push(a.durationMs)
  }
  return out.sort((x, y) => x - y)
}

export function bucketStats(attempts: Attempt[]): BucketStats {
  const durations = itemDurations(attempts)
  const responses = attempts.length
  const correct = attempts.filter((a) => a.correct).length
  const items = durations.length
  return {
    responses,
    correct,
    accuracy: responses > 0 ? correct / responses : 0,
    items,
    avgMs: items > 0 ? durations.reduce((sum, d) => sum + d, 0) / items : 0,
    medianMs: quantile(durations, 0.5),
    p90Ms: quantile(durations, 0.9),
  }
}

export function bucketKey(subtest: Subtest, difficulty: Difficulty): BucketKey {
  return `${subtest}/${difficulty}`
}

export function computeSummary(attempts: Attempt[]): Summary {
  const bySubtest: Partial<Record<Subtest, BucketStats>> = {}
  const byBucket: Partial<Record<BucketKey, BucketStats>> = {}
  const subtestGroups = new Map<Subtest, Attempt[]>()
  const bucketGroups = new Map<BucketKey, Attempt[]>()
  for (const attempt of attempts) {
    const st = (subtestGroups.get(attempt.subtest) ?? []) as Attempt[]
    st.push(attempt)
    subtestGroups.set(attempt.subtest, st)
    const key = bucketKey(attempt.subtest, attempt.difficulty)
    const bg = (bucketGroups.get(key) ?? []) as Attempt[]
    bg.push(attempt)
    bucketGroups.set(key, bg)
  }
  for (const [subtest, group] of subtestGroups) bySubtest[subtest] = bucketStats(group)
  for (const [key, group] of bucketGroups) byBucket[key] = bucketStats(group)
  return { overall: bucketStats(attempts), bySubtest, byBucket }
}

export interface SessionSummary {
  sessionId: string
  responses: number
  correct: number
  accuracy: number
  avgMs: number
  medianMs: number
  firstAnsweredAt: string
  lastAnsweredAt: string
}

/** Session history, oldest first. */
export function computeSessions(attempts: Attempt[]): SessionSummary[] {
  const groups = new Map<string, Attempt[]>()
  for (const attempt of attempts) {
    const g = (groups.get(attempt.sessionId) ?? []) as Attempt[]
    g.push(attempt)
    groups.set(attempt.sessionId, g)
  }
  const out: SessionSummary[] = []
  for (const [sessionId, group] of groups) {
    const stats = bucketStats(group)
    const answeredAt = group.map((a) => a.answeredAt).sort()
    out.push({
      sessionId,
      responses: stats.responses,
      correct: stats.correct,
      accuracy: stats.accuracy,
      avgMs: stats.avgMs,
      medianMs: stats.medianMs,
      firstAnsweredAt: answeredAt[0],
      lastAnsweredAt: answeredAt[answeredAt.length - 1],
    })
  }
  return out.sort((a, b) => a.firstAnsweredAt.localeCompare(b.firstAnsweredAt))
}
